import { useMemo, useState, useCallback } from "react"
import { toast } from "react-toastify"
import { Col, Row, UncontrolledTooltip, Button, Badge } from "reactstrap"

import TableContainer from "components/Common/TableContainer"
import DeleteModal from "components/Common/DeleteModal"
import GlobalInputSearch from "components/Common/GlobalInputSearch"
import PageWrapper from "components/Common/PageWrapper"

import { useListParams } from "hooks/useListParams"
import {
  useCarCareServiceCenters,
  useDeleteCarCareServiceCenter,
} from "hooks/useCarCareServiceCenter"

import { CarCareServiceCenterHeader } from "./CarCareServiceCenterHeader"
import CarCareServiceCenterAddEdit from "./CarCareServiceCenterAddEdit"

const CarCareServiceCenterList = () => {
  document.title = "Car Care Service Centers"

  const [modal, setModal] = useState(false)
  const [isEdit, setIsEdit] = useState(false)
  const [selectedData, setSelectedData] = useState(null)
  const [deleteModal, setDeleteModal] = useState(false)

  const { params, setPage, setSearch, resetParams, hasActiveFilters } =
    useListParams({ page: 1, search: "" })

  const queryParams = useMemo(() => {
    const { page = 1, per_page = 10, search } = params
    return { page, per_page, search }
  }, [params])

  const {
    data: responseData,
    isLoading,
    refetch,
    isRefetching,
  } = useCarCareServiceCenters(queryParams)

  const { mutateAsync: deleteItem, isPending: deleteLoading } =
    useDeleteCarCareServiceCenter()

  const toggleModal = useCallback(() => {
    setModal(prev => !prev)
    if (modal) {
      setSelectedData(null)
      setIsEdit(false)
    }
  }, [modal])

  const handleAddNewClick = () => {
    setSelectedData(null)
    setIsEdit(false)
    setModal(true)
  }

  const handleEditClick = item => {
    setSelectedData(item)
    setIsEdit(true)
    setModal(true)
  }

  const handleDeleteClick = item => {
    setSelectedData(item)
    setDeleteModal(true)
  }

  const handleDelete = async () => {
    if (!selectedData?.id) return
    try {
      const res = await deleteItem(selectedData.id)
      toast.success(res?.message ?? "Service center deleted successfully.")
      setDeleteModal(false)
      refetch()
    } catch (err) {
      console.error(err)
      toast.error("Failed to delete service center.")
    }
  }

  const columns = useMemo(
    () => [
      {
        header: "ID",
        accessorKey: "id",
        size: 60,
        cell: ({ row }) => (
          <div className="text-muted fw-semibold">#{row.original.id}</div>
        ),
      },
      {
        header: "Name",
        accessorKey: "name",
        cell: ({ row }) => (
          <div>
            <div className="fw-semibold">{row.original.name}</div>
            {row.original.short_name && (
              <small className="text-muted">{row.original.short_name}</small>
            )}
          </div>
        ),
      },
      {
        header: "Branch",
        accessorKey: "showroom.name",
        cell: ({ row }) => row.original.showroom?.name || "-",
      },
      {
        header: "Phone",
        accessorKey: "phone",
        cell: ({ row }) => row.original.phone || "-",
      },
      {
        header: "Address",
        accessorKey: "address",
        cell: ({ row }) => row.original.address || "-",
      },
      {
        header: "Status",
        accessorKey: "is_active",
        cell: ({ row }) => (
          <Badge
            className={`badge-soft-${
              row.original.is_active ? "success" : "danger"
            }`}
          >
            {row.original.is_active ? "Active" : "Inactive"}
          </Badge>
        ),
      },
      {
        header: "Action",
        cell: ({ row }) => {
          const data = row.original
          return (
            <div className="d-flex gap-1">
              <Button
                color="info"
                size="sm"
                onClick={() => handleEditClick(data)}
                id={`edit-${data.id}`}
              >
                <i className="mdi mdi-pencil-outline" />
              </Button>
              <UncontrolledTooltip target={`edit-${data.id}`}>
                Edit
              </UncontrolledTooltip>

              <Button
                color="danger"
                size="sm"
                onClick={() => handleDeleteClick(data)}
                id={`delete-${data.id}`}
              >
                <i className="mdi mdi-delete-outline" />
              </Button>
              <UncontrolledTooltip target={`delete-${data.id}`}>
                Delete
              </UncontrolledTooltip>
            </div>
          )
        },
      },
    ],
    []
  )

  const data = responseData?.data?.data || []
  const paginationInfo = responseData?.data

  return (
    <PageWrapper
      title="Car Care Service Centers"
      breadcrumbItem="Service Centers"
    >
      <CarCareServiceCenterHeader
        handleAddNewClick={handleAddNewClick}
        handleRefresh={refetch}
        resetParams={resetParams}
        isRefetching={isRefetching}
        hasActiveFilters={hasActiveFilters}
        total={paginationInfo?.total ?? 0}
      />

      <DeleteModal
        show={deleteModal}
        loading={deleteLoading}
        onDeleteClick={handleDelete}
        onCloseClick={() => setDeleteModal(false)}
      />

      <Row className="my-3">
        <Col sm={4}>
          <GlobalInputSearch value={params.search} onChange={setSearch} />
        </Col>
      </Row>

      <TableContainer
        columns={columns}
        data={data}
        isPagination
        paginationInfo={paginationInfo}
        onPageChange={setPage}
        loading={isLoading || isRefetching}
        className="table-responsive"
      />

      <CarCareServiceCenterAddEdit
        modal={modal}
        isEdit={isEdit}
        editedData={selectedData}
        toggleModal={toggleModal}
      />
    </PageWrapper>
  )
}

export default CarCareServiceCenterList
